import { useEffect, useMemo, useState } from "react";
import { WorkUpdateSchema, type ListItem } from "@yushi-portfolio/api-types";
import type { WorkDocument } from "@yushi-portfolio/content-schema";
import { renderMarkdown } from "@yushi-portfolio/presentation";
import { ErrorList, LocalizedInput, move, RowControls, Select, TextArea, TextInput } from "../components/fields";
import { MediaListEditor } from "../components/MediaListEditor";
import { PublishButton, RevisionList } from "../components/PublishControls";
import { WorkPreview } from "../components/WorkPreview";
import type { ApiClient } from "../lib/api";
import { describeError, useUnsavedGuard, validationErrors } from "../lib/editor";
import {
  emptyInstallation,
  emptyWorkForm,
  rowKey,
  suggestSlug,
  workFromForm,
  workToForm,
  type InstallationForm,
  type WorkForm,
} from "../lib/forms";
import { navigate } from "../lib/router";

const TABS = ["Basic", "Concept", "Media", "Installations", "Technical", "Preview", "History"] as const;
type Tab = (typeof TABS)[number];

const TYPES = [
  { value: "painting", label: "絵画 painting" },
  { value: "video", label: "映像 video" },
  { value: "media-art", label: "メディアアート media-art" },
  { value: "other", label: "その他 other" },
] as const;

export function WorkEditor({ api, id }: { api: ApiClient; id?: string }) {
  const [form, setForm] = useState<WorkForm>();
  const [saved, setSaved] = useState<string>();
  const [published, setPublished] = useState<WorkDocument>();
  const [exhibitions, setExhibitions] = useState<ListItem[]>([]);
  const [tab, setTab] = useState<Tab>("Basic");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<{ message: string; details: string[]; conflict: boolean }>();
  const [notice, setNotice] = useState<string>();
  const [publishedCount, setPublishedCount] = useState(0);

  const load = () => {
    setErrors(undefined);
    if (!id) {
      const empty = emptyWorkForm();
      setForm(empty);
      setSaved(JSON.stringify(workFromForm(empty)));
      return;
    }
    api.getWork(id).then(
      (res) => {
        const next = workToForm(res.draft);
        setForm(next);
        setSaved(JSON.stringify(workFromForm(next)));
        setPublished(res.published);
      },
      (e) => setErrors(describeError(e)),
    );
  };
  useEffect(load, [id]);
  useEffect(() => {
    api.listExhibitions().then((r) => setExhibitions(r.items), () => setExhibitions([]));
  }, []);

  const body = useMemo(() => (form ? workFromForm(form) : undefined), [form]);
  const dirty = body !== undefined && JSON.stringify(body) !== saved;
  useUnsavedGuard(dirty);

  if (!form || !body) return errors ? <ErrorList title={errors.message} errors={errors.details} /> : <p className="muted">読み込み中…</p>;
  const set = (patch: Partial<WorkForm>) => setForm({ ...form, ...patch });

  async function save() {
    if (!body) return;
    setNotice(undefined);
    const clientErrors = validationErrors(WorkUpdateSchema, body);
    if (clientErrors.length) {
      setErrors({ message: "入力内容を確認してください。", details: clientErrors, conflict: false });
      return;
    }
    setBusy(true);
    try {
      if (!id) {
        const { version: _ignored, ...input } = body;
        const res = await api.createWork(input);
        setSaved(JSON.stringify(body));
        navigate(`/works/${encodeURIComponent(res.draft.id)}`);
        return;
      }
      const res = await api.updateWork(id, body);
      const next = workToForm(res.draft);
      setForm(next);
      setSaved(JSON.stringify(workFromForm(next)));
      setErrors(undefined);
      setNotice("下書きを保存しました。");
    } catch (e) {
      setErrors(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  const slugChangedAfterPublish = published && form.slug.trim() !== published.slug;

  return (
    <section>
      <header className="page-head sticky">
        <div>
          <a href="#/works" className="muted">
            ← 作品一覧
          </a>
          <h1>{form.title.ja || "新しい作品"}</h1>
          <p className="muted">
            {published ? <span className="badge published">公開中</span> : <span className="badge">未公開</span>}{" "}
            {id && <span className="mono">{id}</span>} {id && `· version ${form.version}`} {dirty && <span className="badge warn">未保存</span>}
          </p>
        </div>
        <div className="actions">
          <button type="button" onClick={() => setTab("Preview")}>
            Preview
          </button>
          <button type="button" className="primary" disabled={busy || (!dirty && Boolean(id))} onClick={save}>
            {busy ? "保存中…" : "Save Draft"}
          </button>
          {id && (
            <PublishButton
              disabledReason={dirty ? "保存してから公開してください" : undefined}
              publish={() => api.publishWork(id, form.version)}
              onPublished={() => {
                setPublishedCount((n) => n + 1);
                api.getWork(id).then((res) => setPublished(res.published), () => undefined);
              }}
            />
          )}
        </div>
      </header>

      {notice && <div className="notice ok">{notice}</div>}
      {errors && (
        <div className="notice error" role="alert">
          <strong>{errors.message}</strong>
          {errors.details.length > 0 && (
            <ul>
              {errors.details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
          {errors.conflict && (
            <button type="button" onClick={load}>
              再読み込み(未保存の変更は失われます)
            </button>
          )}
        </div>
      )}

      <nav className="tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? "active" : ""} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </nav>

      {tab === "Basic" && (
        <div className="stack">
          <LocalizedInput label="タイトル" required value={form.title} onChange={(title) => set({ title })} />
          <div className="grid-2">
            <TextInput
              label="slug (URL)"
              value={form.slug}
              onChange={(slug) => set({ slug })}
              hint={`公開URL: /works/${form.slug || "…"}/ — タイトルを変えても自動では変わりません`}
            />
            <div className="field">
              <span className="field-label">&nbsp;</span>
              <button type="button" disabled={!form.title.en.trim()} onClick={() => set({ slug: suggestSlug(form.title.en) })}>
                英語タイトルから slug を提案
              </button>
            </div>
          </div>
          {slugChangedAfterPublish && (
            <div className="notice warn">公開中の URL (/works/{published.slug}/) と異なる slug です。公開済みの URL は原則として変更しないでください。</div>
          )}
          <div className="grid-3">
            <Select label="種類" value={form.type} options={TYPES} onChange={(type) => set({ type })} />
            <TextInput label="制作年 (開始)" inputMode="numeric" value={form.dateStart} onChange={(dateStart) => set({ dateStart })} />
            {!form.ongoing && <TextInput label="制作年 (終了)" inputMode="numeric" value={form.dateEnd} onChange={(dateEnd) => set({ dateEnd })} hint="単年なら空欄" />}
          </div>
          <label className="checkbox">
            <input type="checkbox" checked={form.ongoing} onChange={(e) => set({ ongoing: e.target.checked })} />
            継続中 (2024– のように表示)
          </label>
          <LocalizedInput label="素材 / Medium" value={form.medium} onChange={(medium) => set({ medium })} />
          <LocalizedInput label="サイズ / Dimensions" value={form.dimensions} onChange={(dimensions) => set({ dimensions })} />
          <TextInput label="時間 (秒)" inputMode="numeric" value={form.durationSeconds} onChange={(durationSeconds) => set({ durationSeconds })} hint="映像作品のみ。例: 754 → 12分34秒" />
          <fieldset className="localized">
            <legend>クレジット</legend>
            <div className="stack">
              {form.credits.map((c, index) => (
                <div className="card-row" key={c.key}>
                  <RowControls index={index} count={form.credits.length} onMove={(f, t) => set({ credits: move(form.credits, f, t) })} onRemove={() => set({ credits: form.credits.filter((_, i) => i !== index) })} />
                  <LocalizedInput label="役割" value={c.role} onChange={(role) => set({ credits: form.credits.map((x, i) => (i === index ? { ...x, role } : x)) })} />
                  <LocalizedInput label="名前" value={c.name} onChange={(name) => set({ credits: form.credits.map((x, i) => (i === index ? { ...x, name } : x)) })} />
                </div>
              ))}
              <div>
                <button type="button" onClick={() => set({ credits: [...form.credits, { key: rowKey(), role: { ja: "", en: "" }, name: { ja: "", en: "" } }] })}>
                  + クレジットを追加
                </button>
              </div>
            </div>
          </fieldset>
        </div>
      )}

      {tab === "Concept" && (
        <div className="stack">
          <p className="muted">Markdown で記述します。プレビューは公開サイトと同じ変換です。</p>
          {(["ja", "en"] as const).map((lang) => (
            <div className="grid-2" key={lang}>
              <TextArea label={lang === "ja" ? "日本語" : "English"} rows={14} value={form.concept[lang]} onChange={(v) => set({ concept: { ...form.concept, [lang]: v } })} />
              <div className="field">
                <span className="field-label">プレビュー</span>
                <div className="markdown-preview" dangerouslySetInnerHTML={{ __html: renderMarkdown(form.concept[lang]) }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Media" && <MediaListEditor api={api} media={form.media} onChange={(media) => set({ media })} />}

      {tab === "Installations" && (
        <InstallationsEditor api={api} installations={form.installations} exhibitions={exhibitions} onChange={(installations) => set({ installations })} />
      )}

      {tab === "Preview" && <WorkPreview api={api} body={body} id={id} />}

      {tab === "History" && (id ? <RevisionList api={api} kind="works" id={id} reloadKey={publishedCount} /> : <p className="muted">保存後に表示されます。</p>)}

      {tab === "Technical" && (
        <div className="stack">
          <LocalizedInput label="公開する技術情報" value={form.technicalPublic} onChange={(technicalPublic) => set({ technicalPublic })} />
          <TextArea
            label="内部メモ (非公開)"
            rows={10}
            value={form.technicalInternal}
            onChange={(technicalInternal) => set({ technicalInternal })}
            hint="機材の型番、配線、起動手順など。公開サイトには一切出力されません。"
          />
        </div>
      )}
    </section>
  );
}

function InstallationsEditor({
  api,
  installations,
  exhibitions,
  onChange,
}: {
  api: ApiClient;
  installations: InstallationForm[];
  exhibitions: ListItem[];
  onChange: (v: InstallationForm[]) => void;
}) {
  const update = (index: number, patch: Partial<InstallationForm>) => onChange(installations.map((x, i) => (i === index ? { ...x, ...patch } : x)));
  const options = [{ value: "", label: "(展覧会に紐付けない)" }, ...exhibitions.map((e) => ({ value: e.id, label: e.title || e.id }))];

  return (
    <div className="stack">
      <p className="muted">同じ作品の展示ごとの形態を記録します。展覧会を選ぶと会場・年は展覧会から補われます。</p>
      {installations.map((ins, index) => (
        <div className="card-row" key={ins.key}>
          <div className="row-head">
            <strong>展示 #{index + 1}</strong>
            <RowControls index={index} count={installations.length} onMove={(f, t) => onChange(move(installations, f, t))} onRemove={() => onChange(installations.filter((_, i) => i !== index))} />
          </div>
          <div className="grid-2">
            <Select label="展覧会" value={ins.exhibitionId} options={options} onChange={(exhibitionId) => update(index, { exhibitionId })} />
            <TextInput label="年" inputMode="numeric" value={ins.year} onChange={(year) => update(index, { year })} hint="空欄なら展覧会の開始年" />
          </div>
          <LocalizedInput label="タイトル (任意)" value={ins.title} onChange={(title) => update(index, { title })} />
          <LocalizedInput label="会場 (展覧会の会場を上書き)" value={ins.venueOverride} onChange={(venueOverride) => update(index, { venueOverride })} />
          <LocalizedInput label="サイズ" value={ins.dimensions} onChange={(dimensions) => update(index, { dimensions })} />
          <LocalizedInput label="説明 (Markdown)" multiline value={ins.description} onChange={(description) => update(index, { description })} />
          <TextArea label="内部メモ (非公開)" value={ins.technicalInternal} onChange={(technicalInternal) => update(index, { technicalInternal })} />
          <fieldset className="localized">
            <legend>展示風景のメディア</legend>
            <MediaListEditor api={api} media={ins.media} allowFeatured={false} onChange={(media) => update(index, { media })} />
          </fieldset>
        </div>
      ))}
      <div>
        <button type="button" onClick={() => onChange([...installations, emptyInstallation()])}>
          + 展示を追加
        </button>
      </div>
    </div>
  );
}
